import { Component, computed, DestroyRef, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Sort } from '@angular/material/sort';

import {
  DEFAULT_PAGINATION,
  PaginationRequest,
  SortingRequest,
  SortingRestRequest,
} from '@rp/shared/models';
import { filter } from 'rxjs';
import { ObjectUtils } from '@rp/utils';
import { PaginationAdapter, SortingAdapter } from '@rp/shared/adapters';

import { BaseTable } from './base-table';

@Component({
  template: '',
})
export abstract class BaseTablePageComponent {
  tableRef = viewChild<BaseTable>('table');
  arrayKey = signal([]);
  params = computed(() =>
    ObjectUtils.objectToQueryString(
      {
        ...PaginationAdapter.toRest(this._pagination()),
        ...ObjectUtils.stripEmptyFields<SortingRestRequest>(SortingAdapter.toRest(this.sorting())),
      },
      this.arrayKey(),
    ),
  );

  protected isLoading = signal<boolean>(false);
  protected _defaultPagination = DEFAULT_PAGINATION;
  protected _pagination = signal<PaginationRequest>(this._defaultPagination);
  protected _destroyRef = inject(DestroyRef);

  protected readonly sorting = signal<SortingRequest>({});

  protected abstract _getData(): void;

  constructor() {
    toObservable(this.tableRef)
      .pipe(filter(Boolean), takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._setDefaultPaginator());
  }

  handleSortChanged(sort: Sort): void {
    const { direction } = sort;

    const sortMap: Record<string, string> = {
      asc: `${sort.active}`,
      desc: `-${sort.active}`,
    };

    const sorting = sortMap[direction] ? { sorting: sortMap[direction] } : {};

    if (JSON.stringify(this.sorting()) !== JSON.stringify(sorting)) {
      this._resetPaginationToDefaultPage();

      this.sorting.set(sorting);

      this._getData();
    }
  }

  handlePaginationChanged(pagination: PaginationRequest): void {
    this._pagination.set(pagination);

    this._getData();
  }

  protected _resetPaginationToDefaultPage(): void {
    const { limit } = this._pagination();
    const defaultPageNumber = this._defaultPagination.page;
    const defaultPageIndex = defaultPageNumber - 1;
    this._pagination.set({ page: defaultPageNumber, limit });
    this.tableRef()?.setPaginator(defaultPageIndex, limit);
  }

  private _setDefaultPaginator(): void {
    const { limit, page } = this._defaultPagination;
    const defaultPageIndex = page - 1;
    this.tableRef()?.setDefaultPageSize(limit);
    this.tableRef()?.setPaginator(defaultPageIndex, limit);
  }
}
